import { useEffect } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Footer from "./Footer";
import PublicHeader from "./PublicHeader";

// Prompt 16 — public legal pages (Terms / Privacy / Contact).
// Renders the verbatim Markdown passed via `source`:
//  - `remark-gfm` enables tables, autolinks, task lists.
//  - HTML comments (`<!-- ... -->`) are stripped before rendering.
//  - Internal `/terms`, `/privacy`, `/contact` links route through
//    React Router. All other links open in a new tab.
//  - Bare email addresses in paragraph text become `mailto:` links.
//  - A `<title>` is set from `title` so the browser tab is useful.
const INTERNAL_PATHS = new Set(["/terms", "/privacy", "/contact", "/help"]);
const EMAIL_RE = /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

function stripHtmlComments(md) {
  // Multiline-safe; non-greedy so adjacent comments don't collide.
  return md.replace(/<!--[\s\S]*?-->/g, "");
}

// Walk text children of a node and wrap any bare email in a <a mailto:>.
export function linkifyEmails(children) {
  const wrap = (node, keyPrefix) => {
    if (typeof node !== "string") return node;
    if (!node.includes("@")) return node;
    const parts = node.split(EMAIL_RE);
    if (parts.length === 1) return node;
    return parts.map((p, i) => {
      if (EMAIL_RE.test(p)) {
        // RegExp.test with /g flag mutates lastIndex; reset defensively.
        EMAIL_RE.lastIndex = 0;
        return (
          <a key={`${keyPrefix}-${i}`} href={`mailto:${p}`}
             className="underline hover:text-neutral-900">
            {p}
          </a>
        );
      }
      return p;
    });
  };
  if (Array.isArray(children)) {
    return children.map((c, i) => wrap(c, `e-${i}`));
  }
  return wrap(children, "e-0");
}

export function MarkdownLink({ href, children }) {
  const url = href || "";
  // Normalise `https://linguafrancainv.com/terms` → internal `/terms`.
  let internalPath = null;
  if (url.startsWith("/")) internalPath = url;
  else {
    try {
      const u = new URL(url);
      if (u.hostname.endsWith("linguafrancainv.com")
          && INTERNAL_PATHS.has(u.pathname)) {
        internalPath = u.pathname;
      }
    } catch (_e) { /* not a URL */ }
  }
  if (internalPath && INTERNAL_PATHS.has(internalPath)) {
    return (
      <Link to={internalPath} className="underline hover:text-neutral-900">
        {children}
      </Link>
    );
  }
  if (url.startsWith("mailto:")) {
    return <a href={url} className="underline hover:text-neutral-900">{children}</a>;
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
       className="underline hover:text-neutral-900">
      {children}
    </a>
  );
}

export default function LegalPage({ title, source, docTitle, extraComponents }) {
  useEffect(() => {
    const prev = document.title;
    document.title = docTitle || `${title} · Lingua Franca`;
    return () => { document.title = prev; };
  }, [title, docTitle]);

  const md = stripHtmlComments(source || "");

  const components = {
    h1: ({ children }) => (
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-10 mb-6">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl font-semibold mt-10 mb-3 border-b border-neutral-200 pb-2">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold mt-6 mb-2">{children}</h3>
    ),
    p: ({ children }) => (
      <p className="text-base text-neutral-700 leading-relaxed my-3">{linkifyEmails(children)}</p>
    ),
    li: ({ children }) => (
      <li className="text-base text-neutral-700 leading-relaxed my-1">{linkifyEmails(children)}</li>
    ),
    ul: ({ children }) => (
      <ul className="list-disc pl-6 my-3 space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-6 my-3 space-y-1">{children}</ol>
    ),
    hr: () => <hr className="my-8 border-neutral-200" />,
    strong: ({ children }) => <strong className="font-semibold text-neutral-900">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    a: MarkdownLink,
    table: ({ children }) => (
      <div className="my-5 overflow-x-auto">
        <table className="min-w-full border border-neutral-200 text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-neutral-50 border-b border-neutral-200">{children}</thead>
    ),
    th: ({ children }) => (
      <th className="text-left px-3 py-2 label-mini border-r last:border-r-0 border-neutral-200 align-top">{children}</th>
    ),
    td: ({ children }) => (
      <td className="px-3 py-2 border-t border-r last:border-r-0 border-neutral-100 align-top text-neutral-700">
        {linkifyEmails(children)}
      </td>
    ),
    code: ({ children }) => (
      <code className="font-mono text-xs bg-neutral-100 px-1 py-0.5 border border-neutral-200">{children}</code>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-neutral-300 pl-4 my-4 text-neutral-700 italic">{children}</blockquote>
    ),
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />
      <main className="flex-1 w-full max-w-3xl mx-auto px-6 pb-16 font-sans" data-testid={`legal-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ ...components, ...extraComponents }}>
          {md}
        </ReactMarkdown>
      </main>
      <Footer />
    </div>
  );
}
